import { useState, useEffect } from 'react';
import Sidebar from '@/components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Mail, Server, Shield, Save, Send, Loader2, Eye, EyeOff, CheckCircle, ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PlatformSettingsPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState(user?.email || '');
  const [testResult, setTestResult] = useState(null);

  const [smtp, setSmtp] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'DJ CRM',
    smtp_use_tls: true,
  });

  const token = localStorage.getItem('token');

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/platform/smtp-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data && res.data.exists !== false) {
        setSmtp(prev => ({
          ...prev,
          smtp_host: res.data.smtp_host || '',
          smtp_port: res.data.smtp_port || 587,
          smtp_username: res.data.smtp_username || '',
          smtp_password: res.data.smtp_password_set ? '••••••••' : '',
          smtp_from_email: res.data.smtp_from_email || '',
          smtp_from_name: res.data.smtp_from_name || 'DJ CRM',
          smtp_use_tls: res.data.smtp_use_tls !== false,
        }));
      }
    } catch (err) {
      console.error('Failed to load platform SMTP settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/platform/smtp-settings`, smtp, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Platform SMTP settings saved');
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) { toast.error('Enter an email address'); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/api/platform/smtp-settings/test`, 
        { to_email: testEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestResult({ success: true, message: res.data.message });
      toast.success('Test email sent!');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to send test email';
      setTestResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const updateSmtp = (field, value) => {
    setSmtp(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} />
      <div className="flex-1 ml-0 md:ml-64 p-4 md:p-8">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/platform-accounts')} data-testid="back-to-platform">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Platform Settings</h1>
              <p className="text-sm text-muted-foreground">Configure platform-wide email and notifications</p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* SMTP Configuration */}
              <Card className="bg-card border-border" data-testid="smtp-settings-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Server className="w-5 h-5 text-blue-400" />
                    Platform SMTP Settings
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    This SMTP account is used for all platform-level emails: verification, password resets, forwarding, backup notifications, and platform notifications to DJ companies.
                  </p>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>SMTP Host *</Label>
                      <Input
                        value={smtp.smtp_host}
                        onChange={e => updateSmtp('smtp_host', e.target.value)}
                        placeholder="mail.djcrm.net"
                        className="bg-muted/20"
                        data-testid="smtp-host-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>SMTP Port</Label>
                      <Input
                        type="number"
                        value={smtp.smtp_port}
                        onChange={e => updateSmtp('smtp_port', parseInt(e.target.value) || 587)}
                        placeholder="587"
                        className="bg-muted/20"
                        data-testid="smtp-port-input"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Username *</Label>
                      <Input
                        value={smtp.smtp_username}
                        onChange={e => updateSmtp('smtp_username', e.target.value)}
                        placeholder="noreply@djcrm.net"
                        className="bg-muted/20"
                        data-testid="smtp-username-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Password *</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={smtp.smtp_password}
                          onChange={e => updateSmtp('smtp_password', e.target.value)}
                          placeholder="Enter SMTP password"
                          className="bg-muted/20 pr-10"
                          data-testid="smtp-password-input"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>From Email *</Label>
                      <Input
                        value={smtp.smtp_from_email}
                        onChange={e => updateSmtp('smtp_from_email', e.target.value)}
                        placeholder="noreply@djcrm.net"
                        className="bg-muted/20"
                        data-testid="smtp-from-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>From Name</Label>
                      <Input
                        value={smtp.smtp_from_name}
                        onChange={e => updateSmtp('smtp_from_name', e.target.value)}
                        placeholder="DJ CRM"
                        className="bg-muted/20"
                        data-testid="smtp-from-name-input"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3 py-2">
                    <Switch
                      checked={smtp.smtp_use_tls}
                      onCheckedChange={v => updateSmtp('smtp_use_tls', v)}
                      data-testid="smtp-tls-switch"
                    />
                    <div>
                      <Label className="cursor-pointer">Use STARTTLS</Label>
                      <p className="text-xs text-muted-foreground">Enable for port 587 (STARTTLS). Disable for port 465 (SSL/TLS).</p>
                    </div>
                  </div>

                  <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto" data-testid="save-smtp-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save SMTP Settings
                  </Button>
                </CardContent>
              </Card>

              {/* Test Email */}
              <Card className="bg-card border-border" data-testid="test-email-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Mail className="w-5 h-5 text-green-400" />
                    Send Test Email
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Verify your SMTP settings by sending a test email. Save your settings first.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-3">
                    <Input
                      type="email"
                      value={testEmail}
                      onChange={e => setTestEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="bg-muted/20 flex-1"
                      data-testid="test-email-input"
                    />
                    <Button onClick={handleTestEmail} disabled={testing} variant="outline" data-testid="send-test-email-btn">
                      {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Send Test
                    </Button>
                  </div>

                  {testResult && (
                    <div
                      className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
                        testResult.success
                          ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                          : 'bg-red-500/10 text-red-400 border border-red-500/20'
                      }`}
                      data-testid="test-email-result"
                    >
                      {testResult.success ? <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" /> : <Shield className="w-4 h-4 mt-0.5 shrink-0" />}
                      <span>{testResult.message}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Info about usage */}
              <Card className="bg-card border-border">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-amber-400" />
                    What uses this SMTP?
                  </h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-blue-400 shrink-0" /> Account verification emails for new DJ companies</li>
                    <li className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-blue-400 shrink-0" /> Password reset emails for platform users</li>
                    <li className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-blue-400 shrink-0" /> Email forwarding from website builder domains</li>
                    <li className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-blue-400 shrink-0" /> Automated backup notification emails</li>
                    <li className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-blue-400 shrink-0" /> Photo retention cleanup alerts</li>
                    <li className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-blue-400 shrink-0" /> Platform notifications to DJ companies</li>
                  </ul>
                  <div className="mt-4 p-3 rounded-lg bg-muted/20 text-xs text-muted-foreground">
                    <strong>Note:</strong> Individual DJ companies use their own email settings (configured in their Settings page) for client communications, booking confirmations, and invoices.
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
